import React from 'react';
import {
  Action,
  Fieldset, FormHandlers, FormSkeleton, ICON_ALERT_CIRCLE_OUTLINE, ICON_CHECK_CIRCLE_OUTLINE, Message, Portal, Spacing, Stack, useModal,
} from '@doveit/bricks';
import hash from 'object-hash';
import { OfferWizardStepProps } from '../types';
import {
  nonLegalBuyerAmlFormModelToOfferWizardBuyerContactData, legalEntityAmlFormModelToOfferWizardData, offerWizardBuyerContactDataToNonLegalBuyerAmlFormModel, offerWizardDataToLegalEntityAmlFormModel,
} from './mappers';
import ViewContactPreview from '../../../../contact/containers/view-contact-preview/ViewContactPreview';
import { isEmpty } from '../../../../utils/object/object';
import { OfferWizardData, WizardContact } from '../../../../providers/api/dtos';
import SimpleModal from '../../../../components/simple-modal/SimpleModal';
import NonLegalBuyerAmlForm, { NonLegalBuyerAmlFormModel } from './non-legal-buyer-aml-form/NonLegalBuyerAmlForm';
import LegalEntityAmlForm, { LegalEntityAmlFormModel } from '../../../../contact/components/legal-entity-aml-form/LegalEntityAmlForm';

const OfferWizardAntiMoneyLaundering: React.FC<OfferWizardStepProps> = ({
  initialData,
  loading,
  formRef,
  onSubmit,
}) => {
  const legalBuyerStepRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const nonLegalBuyerAmlFormRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  const nonLegalBuyerAmlModal = useModal<{ buyer: WizardContact }>();

  const [isLegalStepDirty, setIsLegalStepDirty] = React.useState(false);
  const [offerWizardData, setOfferWizardData] = React.useState(initialData);

  React.useEffect(() => {
    setOfferWizardData(initialData);
  }, [initialData]);

  const isLegalBuyer = React.useMemo(
    () => offerWizardData.buyerContacts?.length === 1 && !!offerWizardData.buyerContacts[0].legal,
    [offerWizardData.buyerContacts],
  );

  const nonLegalBuyers = React.useMemo(
    () => (!isLegalBuyer ? (offerWizardData.buyerContacts?.filter(({ legal }) => !legal) || []) : []),
    [isLegalBuyer, offerWizardData.buyerContacts],
  );

  React.useImperativeHandle(formRef, () => ({
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => {
      if (isLegalBuyer) {
        legalBuyerStepRef.current?.handleSubmit(e);
      } else {
        onSubmit(offerWizardData, isLegalStepDirty);
      }
    },
  } as FormHandlers), [isLegalStepDirty, isLegalBuyer, offerWizardData, onSubmit]);

  const handleLegalBuyerFormSubmit = React.useCallback((formValues: LegalEntityAmlFormModel) => {
    const updatedOfferWizardData = legalEntityAmlFormModelToOfferWizardData(offerWizardData, formValues);

    const isFormDirty = (legalBuyerStepRef?.current as any)?.dirty ?? false;

    onSubmit(updatedOfferWizardData, isFormDirty);
  }, [offerWizardData, onSubmit]);

  const buyerHasMissingAml = React.useCallback(
    (buyer: WizardContact) => !buyer.antiMoneyLaundering || isEmpty(buyer),
    [],
  );

  const message = React.useMemo(() => {
    if (isLegalBuyer) return undefined;

    if (nonLegalBuyers.length === 0) {
      return 'Per compilare la dichiarazione di antiriciclaggio è necessario selezionare o inserire un compratore.';
    }

    const nonLegalBuyersWithMissingAml = nonLegalBuyers.filter(buyerHasMissingAml);

    switch (nonLegalBuyersWithMissingAml.length) {
      case 0:
        return undefined;
      case 1:
        return 'Compila i dati relativi all\'antiriciclaggio per il compratore.';
      default:
        return 'Compila i dati relativi all\'antiriciclaggio per ogni compratore.';
    }
  }, [buyerHasMissingAml, isLegalBuyer, nonLegalBuyers]);

  const handleBuyerAmlUpsert = React.useCallback((formValues: NonLegalBuyerAmlFormModel, buyerToAddAmlTo?: WizardContact) => {
    if (!buyerToAddAmlTo) return;

    const updatedOfferWizardData: OfferWizardData = {
      ...offerWizardData,
      excludeAntiMoneyLaundering: false,
      buyerContacts: offerWizardData.buyerContacts?.map((buyer) => (hash(buyerToAddAmlTo) === hash(buyer)
        ? nonLegalBuyerAmlFormModelToOfferWizardBuyerContactData(buyer, formValues)
        : buyer)),
    };

    setOfferWizardData(updatedOfferWizardData);
    setIsLegalStepDirty(true);

    nonLegalBuyerAmlModal.close();
  }, [nonLegalBuyerAmlModal, offerWizardData]);

  if (loading) return <FormSkeleton />;

  return (
    <>
      <Stack gap={300}>
        {message && (
          <Message
            type="neutral"
            message={message}
          />
        )}

        {isLegalBuyer && (
          <LegalEntityAmlForm
            buyer={offerWizardData.buyerContacts![0]}
            subjectLabel="Il compratore"
            innerRef={legalBuyerStepRef}
            onSubmit={handleLegalBuyerFormSubmit}
            initialValues={offerWizardDataToLegalEntityAmlFormModel(offerWizardData)}
          />
        )}

        {!isLegalBuyer && (
          <>
            {nonLegalBuyers.length > 1 && <Fieldset legend="Elenco compratori" />}

            <Stack gap={150}>
              {nonLegalBuyers.map((buyer) => (
                <ViewContactPreview
                  key={buyer.id}
                  title="Antiriciclaggio"
                  color={buyerHasMissingAml(buyer) ? 'critical' : 'success'}
                  icon={{
                    path: buyerHasMissingAml(buyer) ? ICON_ALERT_CIRCLE_OUTLINE : ICON_CHECK_CIRCLE_OUTLINE,
                  }}
                  contactId={buyer.id!}
                  primaryActions={[
                    <Action
                      label={buyerHasMissingAml(buyer) ? 'Compila' : 'Modifica'}
                      onClick={() => nonLegalBuyerAmlModal.open({ buyer })}
                      size="S"
                    />,
                  ]}
                />
              ))}
            </Stack>
          </>
        )}
      </Stack>

      <Portal>
        {nonLegalBuyerAmlModal.data?.buyer && (
          <SimpleModal
            {...nonLegalBuyerAmlModal}
            title={`${buyerHasMissingAml(nonLegalBuyerAmlModal.data.buyer) ? 'Compila' : 'Modifica'} dati antiriciclaggio`}
            aria-label={`${buyerHasMissingAml(nonLegalBuyerAmlModal.data.buyer) ? 'Compila' : 'Modifica'} dati antiriciclaggio`}
            footer={(
              <Action
                label={buyerHasMissingAml(nonLegalBuyerAmlModal.data.buyer) ? 'Compila' : 'Modifica'}
                color="primary"
                emphasis="high"
                aria-label="Pulsante per salvare i dati relativi all'antiriciclaggio"
                onClick={() => nonLegalBuyerAmlFormRef.current?.handleSubmit()}
              />
            )}
          >
            <ViewContactPreview contactId={nonLegalBuyerAmlModal.data.buyer.id!} />
            <Spacing margin={[300, 0, 0]}>
              <NonLegalBuyerAmlForm
                initialValues={offerWizardBuyerContactDataToNonLegalBuyerAmlFormModel(nonLegalBuyerAmlModal.data.buyer)}
                onSubmit={(formValues) => handleBuyerAmlUpsert(formValues, nonLegalBuyerAmlModal.data?.buyer)}
                formRef={nonLegalBuyerAmlFormRef}
              />
            </Spacing>
          </SimpleModal>
        )}
      </Portal>
    </>
  );
};

export default OfferWizardAntiMoneyLaundering;
